import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, PlusIcon, CalendarIcon, SearchIcon, HomeIcon, UserIcon, PhoneIcon, ClockIcon, RefreshCwIcon, XCircleIcon, MessageSquareIcon, ChevronDownIcon, CheckSquareIcon, PenToolIcon, DollarSignIcon, BookOpenIcon, PlayCircleIcon } from 'lucide-react';
import { useRental } from '../../contexts/RentalContext';
import { SelectViewingTypeModal } from '../../components/rental/SelectViewingTypeModal';
import { AddViewingDrawer } from '../../components/rental/AddViewingDrawer';
import { RescheduleViewingModal } from '../../components/rental/RescheduleViewingModal';
import { CancelViewingModal } from '../../components/rental/CancelViewingModal';
import { CompleteViewingModal } from '../../components/rental/CompleteViewingModal';
import { AwaitingResponseModal } from '../../components/rental/AwaitingResponseModal';
import { ScheduleSigningModal } from '../../components/rental/ScheduleSigningModal';
import { CommissionCollectionModal } from '../../components/rental/CommissionCollectionModal';
import { ViewingDetailsDrawer } from '../../components/rental/ViewingDetailsDrawer';
import { ViewingCountdown } from '../../components/rental/ViewingCountdown';
import { MiniCalendar } from '../../components/rental/MiniCalendar';
import { ViewingChecklist } from '../../components/ViewingChecklist';
import { toast } from 'sonner';
type ViewingStatus = 'scheduled' | 'rescheduled' | 'completed' | 'cancelled' | 'awaiting_response' | 'signing_scheduled' | 'signed_invoicing' | 'commission_collected';
type TabFilter = 'all' | 'scheduled' | 'awaiting_response' | 'signing_scheduled' | 'signed_invoicing' | 'completed' | 'cancelled' | 'needs_feedback';
export function Viewings() {
  const {
    viewings,
    addViewing,
    updateViewing
  } = useRental();
  const navigate = useNavigate();
  // Modal states
  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingViewing, setEditingViewing] = useState<any>(null);
  const [selectedViewingType, setSelectedViewingType] = useState<'inchiriere' | 'vanzare'>('inchiriere');
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [isScheduleSigningModalOpen, setIsScheduleSigningModalOpen] = useState(false);
  const [isRescheduleSigningModalOpen, setIsRescheduleSigningModalOpen] = useState(false);
  const [isCommissionModalOpen, setIsCommissionModalOpen] = useState(false);
  const [selectedViewing, setSelectedViewing] = useState<any>(null);
  const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false);
  const [isAwaitingResponseModalOpen, setIsAwaitingResponseModalOpen] = useState(false);
  const [skipMainDecision, setSkipMainDecision] = useState(false);
  // Filter states
  const [activeTab, setActiveTab] = useState<TabFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState<'today' | 'week' | 'month' | 'all'>('all');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [showChecklist, setShowChecklist] = useState(false);
  const today = new Date().toISOString().split('T')[0];
  // Calculate needs feedback count - helper function (MOVED BEFORE filteredViewings)
  const isNeedsFeedback = (viewing: any) => {
    if (viewing.status !== 'scheduled' && viewing.status !== 'rescheduled') return false;
    const viewingDateTime = new Date(`${viewing.date}T${viewing.time}`);
    const now = new Date();
    return viewingDateTime.getTime() <= now.getTime();
  };
  // Calculate KPI stats
  const stats = [{
    label: 'Vizionări Azi',
    value: viewings.filter(v => v.date === today).length,
    color: 'text-blue-600',
    bg: 'bg-blue-100'
  }, {
    label: 'Programate',
    value: viewings.filter(v => v.status === 'scheduled' || v.status === 'rescheduled').length,
    color: 'text-emerald-600',
    bg: 'bg-emerald-100'
  }, {
    label: 'Aștept Răspuns',
    value: viewings.filter(v => v.status === 'awaiting_response').length,
    color: 'text-blue-600',
    bg: 'bg-blue-100'
  }, {
    label: 'Semnări',
    value: viewings.filter(v => v.status === 'signing_scheduled' || v.status === 'signed_invoicing').length,
    color: 'text-purple-600',
    bg: 'bg-purple-100'
  }, {
    label: 'Finalizate',
    value: viewings.filter(v => v.status === 'commission_collected').length,
    color: 'text-green-600',
    bg: 'bg-green-100'
  }];
  // Filter viewings
  const filteredViewings = viewings.filter(viewing => {
    // Tab filtering with new logic
    if (activeTab !== 'all') {
      if (activeTab === 'scheduled') {
        // Programate includes both scheduled and rescheduled (but NOT past ones)
        if (viewing.status !== 'scheduled' && viewing.status !== 'rescheduled') return false;
        // Exclude viewings that need feedback (they're in their own tab)
        if (isNeedsFeedback(viewing)) return false;
      } else if (activeTab === 'completed') {
        // Finalizate includes commission_collected AND completed (rejected viewings)
        if (viewing.status !== 'commission_collected' && viewing.status !== 'completed') return false;
      } else if (activeTab === 'needs_feedback') {
        // Needs feedback: scheduled/rescheduled viewings that are past their time
        if (!isNeedsFeedback(viewing)) return false;
      } else {
        // Other tabs: exact match
        if (viewing.status !== activeTab) return false;
      }
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!viewing.clientName.toLowerCase().includes(query) && !viewing.clientPhone.includes(query) && !viewing.propertyId.toLowerCase().includes(query)) {
        return false;
      }
    }
    if (dateFilter !== 'all') {
      const viewingDate = new Date(viewing.date);
      const now = new Date();
      if (dateFilter === 'today') {
        if (viewing.date !== today) return false;
      } else if (dateFilter === 'week') {
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - now.getDay());
        if (viewingDate < weekStart) return false;
      } else if (dateFilter === 'month') {
        if (viewingDate.getMonth() !== now.getMonth()) return false;
      }
    }
    if (selectedDate && viewing.date !== selectedDate) return false;
    return true;
  });
  // Calculate needs feedback count
  const needsFeedbackCount = viewings.filter(isNeedsFeedback).length;
  const handleSelectType = (type: 'inchiriere' | 'vanzare') => {
    setSelectedViewingType(type);
    setIsTypeModalOpen(false);
    setIsAddDrawerOpen(true);
  };
  // Check if user has completed at least one rental transaction
  const hasCompletedRentalTransaction = viewings.some(v => v.status === 'commission_collected' && v.viewingType === 'inchiriere');
  const handleDrawerSubmit = (viewingData: any) => {
    if (isEditMode && editingViewing) {
      // Update existing viewing
      updateViewing(editingViewing.id, {
        leadId: viewingData.leadId,
        clientName: viewingData.clientName,
        clientPhone: viewingData.clientPhone,
        propertyId: viewingData.propertyId,
        ownerName: viewingData.ownerName,
        ownerPhone: viewingData.ownerPhone,
        date: viewingData.viewingDate,
        time: viewingData.viewingTime,
        commissionPaidBy: viewingData.commissionPaidBy,
        ownerCommission: viewingData.ownerCommission,
        ownerVatOption: viewingData.ownerVatOption,
        ownerDiscountRequested: viewingData.ownerDiscountRequested,
        clientCommission: viewingData.clientCommission,
        clientVatOption: viewingData.clientVatOption,
        clientDiscountRequested: viewingData.clientDiscountRequested,
        agentNotes: viewingData.notes,
        ownerId: viewingData.ownerId,
        propertyAddress: viewingData.propertyAddress,
        propertyPrice: viewingData.propertyPrice,
        clientContractSigned: viewingData.clientContractOption === 'signed',
        clientContractFile: viewingData.clientContractFile,
        clientSignPhysically: viewingData.clientContractOption === 'physical_signing',
        ownerContractSigned: viewingData.ownerContractOption === 'signed',
        ownerContractFile: viewingData.ownerContractFile,
        ownerSignPhysically: viewingData.ownerContractOption === 'physical_signing',
        clientIsExternal: viewingData.clientIsExternal,
        clientAgencyName: viewingData.clientAgencyName,
        clientAgentName: viewingData.clientAgentName,
        clientAgentPhone: viewingData.clientAgentPhone,
        ownerIsExternal: viewingData.ownerIsExternal,
        ownerAgencyName: viewingData.ownerAgencyName,
        ownerAgentName: viewingData.ownerAgentName,
        ownerAgentPhone: viewingData.ownerAgentPhone
      });
      toast.success('✅ Vizionare actualizată cu succes!');
      setIsEditMode(false);
      setEditingViewing(null);
    } else {
      // Create new viewing
      const newViewing = {
        id: `V${Date.now()}`,
        leadId: viewingData.leadId,
        clientName: viewingData.clientName,
        clientPhone: viewingData.clientPhone,
        propertyId: viewingData.propertyId,
        ownerName: viewingData.ownerName,
        ownerPhone: viewingData.ownerPhone,
        date: viewingData.viewingDate,
        time: viewingData.viewingTime,
        duration: 30,
        status: 'scheduled' as const,
        meetingPoint: 'La proprietate',
        commissionPaidBy: viewingData.commissionPaidBy,
        ownerCommission: viewingData.ownerCommission,
        ownerVatOption: viewingData.ownerVatOption,
        ownerDiscountRequested: viewingData.ownerDiscountRequested,
        clientCommission: viewingData.clientCommission,
        clientVatOption: viewingData.clientVatOption,
        clientDiscountRequested: viewingData.clientDiscountRequested,
        agentNotes: viewingData.notes,
        createdAt: new Date().toISOString(),
        ownerId: viewingData.ownerId,
        propertyAddress: viewingData.propertyAddress,
        propertyPrice: viewingData.propertyPrice,
        viewingType: selectedViewingType,
        clientContractSigned: viewingData.clientContractOption === 'signed',
        clientContractFile: viewingData.clientContractFile,
        clientSignPhysically: viewingData.clientContractOption === 'physical_signing',
        ownerContractSigned: viewingData.ownerContractOption === 'signed',
        ownerContractFile: viewingData.ownerContractFile,
        ownerSignPhysically: viewingData.ownerContractOption === 'physical_signing',
        clientIsExternal: viewingData.clientIsExternal,
        clientAgencyName: viewingData.clientAgencyName,
        clientAgentName: viewingData.clientAgentName,
        clientAgentPhone: viewingData.clientAgentPhone,
        ownerIsExternal: viewingData.ownerIsExternal,
        ownerAgencyName: viewingData.ownerAgencyName,
        ownerAgentName: viewingData.ownerAgentName,
        ownerAgentPhone: viewingData.ownerAgentPhone
      };
      addViewing(newViewing);
      toast.success('✅ Vizionare programată cu succes!');
    }
    setIsAddDrawerOpen(false);
  };
  const handleViewDetails = (viewing: any) => {
    setSelectedViewing(viewing);
    setIsDetailsDrawerOpen(true);
  };
  const handleReschedule = (viewing: any) => {
    setSelectedViewing(viewing);
    setIsDetailsDrawerOpen(false);
    setIsRescheduleModalOpen(true);
  };
  const handleRescheduleSubmit = (data: {
    date: string;
    time: string;
    reason: string;
  }) => {
    if (selectedViewing) {
      updateViewing(selectedViewing.id, {
        date: data.date,
        time: data.time,
        status: 'rescheduled',
        rescheduleReason: data.reason,
        rescheduledAt: new Date().toISOString()
      });
      toast.success('✅ Vizionare reprogramată cu succes!');
    }
    setIsRescheduleModalOpen(false);
    setSelectedViewing(null);
  };
  const handleCancel = (viewing: any) => {
    setSelectedViewing(viewing);
    setIsDetailsDrawerOpen(false);
    setIsCancelModalOpen(true);
  };
  const handleCancelSubmit = (reason: string) => {
    if (selectedViewing) {
      updateViewing(selectedViewing.id, {
        status: 'cancelled',
        cancelReason: reason,
        cancelledAt: new Date().toISOString()
      });
      toast.success('❌ Vizionare anulată');
    }
    setIsCancelModalOpen(false);
    setSelectedViewing(null);
  };
  const handleComplete = (viewing: any) => {
    setSelectedViewing(viewing);
    setIsDetailsDrawerOpen(false);
    setIsCompleteModalOpen(true);
  };
  const handleCompleteSubmit = (data: {
    clientLikes: boolean;
    feedback: string;
    nextStep: 'awaiting_response' | 'schedule_signing' | 'rematching';
  }) => {
    if (selectedViewing) {
      if (data.nextStep === 'schedule_signing') {
        // Open schedule signing modal
        setIsCompleteModalOpen(false);
        setIsScheduleSigningModalOpen(true);
      } else {
        // Update status directly
        updateViewing(selectedViewing.id, {
          status: data.nextStep === 'awaiting_response' ? 'awaiting_response' : 'completed',
          clientLikes: data.clientLikes,
          completionFeedback: data.feedback,
          nextStep: data.nextStep,
          completedAt: new Date().toISOString()
        });
        if (data.nextStep === 'awaiting_response') {
          toast.success('⏳ Vizionare finalizată - Aștept răspuns client');
        } else {
          toast.success('✅ Vizionare finalizată - Client redirecționat către rematching');
        }
        setIsCompleteModalOpen(false);
        setSelectedViewing(null);
      }
    }
  };
  const handleScheduleSigning = (data: {
    date: string;
    time: string;
    location: string;
    notes: string;
  }) => {
    if (selectedViewing) {
      updateViewing(selectedViewing.id, {
        status: 'signing_scheduled',
        signingDate: data.date,
        signingTime: data.time,
        signingLocation: data.location,
        signingNotes: data.notes,
        signingScheduledAt: new Date().toISOString()
      });
      toast.success('📝 Semnare programată cu succes!');
    }
    setIsScheduleSigningModalOpen(false);
    setSelectedViewing(null);
  };
  const handleRescheduleSigning = (viewing: any) => {
    setSelectedViewing(viewing);
    setIsDetailsDrawerOpen(false);
    setIsRescheduleSigningModalOpen(true);
  };
  const handleRescheduleSigningSubmit = (data: {
    date: string;
    time: string;
    location: string;
    notes: string;
  }) => {
    if (selectedViewing) {
      updateViewing(selectedViewing.id, {
        signingDate: data.date,
        signingTime: data.time,
        signingLocation: data.location,
        signingNotes: data.notes,
        signingRescheduledAt: new Date().toISOString()
      });
      toast.success('✅ Semnare reprogramată cu succes!');
    }
    setIsRescheduleSigningModalOpen(false);
    setSelectedViewing(null);
  };
  const handleMarkSignedInvoicing = (viewing: any) => {
    setSelectedViewing(viewing);
    updateViewing(viewing.id, {
      status: 'signed_invoicing',
      signedAt: new Date().toISOString()
    });
    toast.success('✅ Contract semnat - Aștept facturare');
    setIsDetailsDrawerOpen(false);
  };
  const handleCollectCommission = (viewing: any) => {
    setSelectedViewing(viewing);
    setIsDetailsDrawerOpen(false);
    setIsCommissionModalOpen(true);
  };
  const handleCommissionSubmit = (data: {
    ownerCommissionAmount?: number;
    clientCommissionAmount?: number;
    totalCommission: number;
    notes: string;
  }) => {
    if (selectedViewing) {
      updateViewing(selectedViewing.id, {
        status: 'commission_collected',
        ownerCommissionAmount: data.ownerCommissionAmount,
        clientCommissionAmount: data.clientCommissionAmount,
        totalCommission: data.totalCommission,
        commissionNotes: data.notes,
        commissionCollectedAt: new Date().toISOString()
      });
      toast.success(`✅ Comision încasat: ${data.totalCommission.toFixed(2)} EUR`);
    }
    setIsCommissionModalOpen(false);
    setSelectedViewing(null);
  };
  const handleEdit = (viewing: any) => {
    setEditingViewing(viewing);
    setIsEditMode(true);
    setIsDetailsDrawerOpen(false);
    setIsAddDrawerOpen(true);
  };
  const handleClientResponse = (viewing: any) => {
    setSelectedViewing(viewing);
    setIsDetailsDrawerOpen(false);
    setSkipMainDecision(true);
    setIsAwaitingResponseModalOpen(true);
  };
  const handleClientResponseScheduleSigning = () => {
    setIsAwaitingResponseModalOpen(false);
    setIsScheduleSigningModalOpen(true);
  };
  const handleClientResponseReject = (reason: 'wants_different' | 'rented_elsewhere' | 'no_collaboration', notes: string) => {
    if (selectedViewing) {
      const statusMap = {
        wants_different: 'completed',
        rented_elsewhere: 'completed',
        no_collaboration: 'cancelled'
      };
      const feedbackMap = {
        wants_different: `Clientul dorește alt imobil. ${notes}`,
        rented_elsewhere: `Clientul a închiriat altă proprietate. ${notes}`,
        no_collaboration: `Clientul nu mai colaborează. ${notes}`
      };
      updateViewing(selectedViewing.id, {
        status: statusMap[reason],
        clientLikes: false,
        completionFeedback: feedbackMap[reason],
        nextStep: reason === 'wants_different' ? 'rematching' : undefined,
        completedAt: new Date().toISOString(),
        rejectionReason: reason,
        rejectionNotes: notes
      });
      if (reason === 'wants_different') {
        toast.success('✅ Vizionare finalizată - Client redirecționat către rematching');
      } else if (reason === 'rented_elsewhere') {
        toast.success('❌ Vizionare finalizată - Client pierdut');
      } else {
        toast.success('❌ Vizionare anulată - Client nu mai colaborează');
      }
    }
    setIsAwaitingResponseModalOpen(false);
    setSelectedViewing(null);
  };
  const handleDateClick = (date: string) => {
    setSelectedDate(date === selectedDate ? null : date);
  };
  const getStatusBadge = (status: ViewingStatus) => {
    const config = {
      scheduled: {
        label: 'Programată',
        color: 'bg-blue-100 text-blue-700'
      },
      rescheduled: {
        label: 'Reprogramată',
        color: 'bg-orange-100 text-orange-700'
      },
      completed: {
        label: 'Finalizată',
        color: 'bg-slate-100 text-slate-700'
      },
      cancelled: {
        label: 'Anulată',
        color: 'bg-red-100 text-red-700'
      },
      awaiting_response: {
        label: 'Aștept Răspuns',
        color: 'bg-blue-100 text-blue-700'
      },
      signing_scheduled: {
        label: 'Semnare Programată',
        color: 'bg-purple-100 text-purple-700'
      },
      signed_invoicing: {
        label: 'Semnat - Facturare',
        color: 'bg-indigo-100 text-indigo-700'
      },
      commission_collected: {
        label: 'Comision Încasat',
        color: 'bg-emerald-100 text-emerald-700'
      }
    };
    const {
      label,
      color
    } = config[status];
    return <span className={`px-2 py-1 rounded-full text-xs font-bold ${color}`} data-id="element-11202">
        {label}
      </span>;
  };
  return <div className="min-h-screen pb-20" data-id="element-11203">
      {/* Hero Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#132C53] to-[#1e4278] pb-16 pt-10" data-id="element-11204">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]" data-id="element-11205" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-11206">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-11207">
            <div className="text-center md:text-left" data-id="element-11208">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-2 tracking-tight" data-id="element-11209">
                Vizionări{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#357BEB] to-[#21A366]" data-id="element-11210">
                  Închiriere
                </span>
              </h1>
              <p className="text-lg text-slate-300 max-w-2xl" data-id="element-11211">
                Gestionează programările și vizionările proprietăților
              </p>
            </div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.4
          }} className="flex items-center space-x-3" data-id="element-11212">
              <button onClick={() => navigate('/inchiriere/vizionari/tutorial')} className="flex items-center space-x-2 px-5 py-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-xl font-bold shadow-lg hover:shadow-2xl hover:scale-105 transition-all" data-id="element-11213">
                <BookOpenIcon className="w-5 h-5" data-id="element-11214" />
                <span data-id="element-11215">Tutorial</span>
              </button>
              <button onClick={() => setIsAddDrawerOpen(true)} className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-[#357BEB] to-[#21A366] text-white rounded-xl font-bold shadow-lg hover:shadow-2xl hover:scale-105 transition-all" data-id="element-11216">
                <PlusIcon className="w-5 h-5" data-id="element-11217" />
                <span data-id="element-11218">Adaugă Vizionare</span>
              </button>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10" data-id="element-11219">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8" data-id="element-11220">
          {stats.map((stat, index) => <motion.div key={stat.label} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.1
        }} className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-slate-100" data-id="element-11221">
              <p className="text-sm text-slate-500 font-medium mb-1" data-id="element-11222">
                {stat.label}
              </p>
              <div className="flex items-center space-x-2" data-id="element-11223">
                <span className={`text-2xl sm:text-3xl font-bold ${stat.color}`} data-id="element-11224">
                  {stat.value}
                </span>
              </div>
            </motion.div>)}
        </div>

        {/* Tabs (Pipeline) */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 mb-6 overflow-hidden" data-id="element-11225">
          <div className="flex overflow-x-auto scrollbar-hide" data-id="element-11226">
            {[{
            id: 'all',
            label: 'Toate',
            count: viewings.length,
            color: 'blue'
          }, {
            id: 'scheduled',
            label: 'Programate',
            count: viewings.filter(v => (v.status === 'scheduled' || v.status === 'rescheduled') && !isNeedsFeedback(v)).length,
            color: 'emerald'
          }, {
            id: 'awaiting_response',
            label: 'Aștept Răspuns',
            count: viewings.filter(v => v.status === 'awaiting_response').length,
            color: 'blue'
          }, {
            id: 'signing_scheduled',
            label: 'Semnări',
            count: viewings.filter(v => v.status === 'signing_scheduled').length,
            color: 'purple'
          }, {
            id: 'signed_invoicing',
            label: 'Facturare',
            count: viewings.filter(v => v.status === 'signed_invoicing').length,
            color: 'indigo'
          }, {
            id: 'completed',
            label: 'Finalizate',
            count: viewings.filter(v => v.status === 'commission_collected' || v.status === 'completed').length,
            color: 'green'
          }, {
            id: 'cancelled',
            label: 'Anulate',
            count: viewings.filter(v => v.status === 'cancelled').length,
            color: 'red'
          }, {
            id: 'needs_feedback',
            label: 'Necesită Feedback',
            count: needsFeedbackCount,
            color: 'orange'
          }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as TabFilter)} className={`relative flex-shrink-0 px-6 py-4 text-sm font-bold transition-all ${activeTab === tab.id ? `text-${tab.color}-700 bg-${tab.color}-50` : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-11227">
                <div className="flex items-center space-x-2" data-id="element-11228">
                  <span data-id="element-11229">{tab.label}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${activeTab === tab.id ? `bg-${tab.color}-100 text-${tab.color}-700` : 'bg-slate-100 text-slate-600'}`} data-id="element-11230">
                    {tab.count}
                  </span>
                </div>
                {activeTab === tab.id && <motion.div layoutId="activeTab" className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${tab.color === 'blue' ? 'from-blue-500 to-indigo-600' : tab.color === 'emerald' ? 'from-emerald-500 to-green-600' : tab.color === 'purple' ? 'from-purple-500 to-pink-600' : tab.color === 'indigo' ? 'from-indigo-500 to-blue-600' : tab.color === 'green' ? 'from-green-500 to-emerald-600' : tab.color === 'red' ? 'from-red-500 to-rose-600' : 'from-orange-500 to-amber-600'}`} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-11231" />}
              </button>)}
          </div>
        </div>

        {/* Search & Filters */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-4 mb-6" data-id="element-11232">
          <div className="flex flex-col md:flex-row gap-4" data-id="element-11233">
            <div className="flex-1 relative" data-id="element-11234">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-11235" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Caută client / telefon / ID proprietate..." className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-11236" />
            </div>

            <div className="relative" data-id="element-11237">
              <select value={dateFilter} onChange={e => setDateFilter(e.target.value as any)} className="appearance-none px-4 py-2 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white font-medium" data-id="element-11238">
                <option value="all" data-id="element-11239">Toate perioadele</option>
                <option value="today" data-id="element-11240">Azi</option>
                <option value="week" data-id="element-11241">Săptămâna aceasta</option>
                <option value="month" data-id="element-11242">Luna aceasta</option>
              </select>
              <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" data-id="element-11243" />
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-11244">
          {/* CARD GRID VIEW */}
          <div className="lg:col-span-2" data-id="element-11245">
            {selectedDate && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-xl flex items-center justify-between mb-6" data-id="element-11246">
                <div data-id="element-11247">
                  <p className="text-sm font-bold text-blue-900" data-id="element-11248">
                    Filtrare activă:{' '}
                    {new Date(selectedDate).toLocaleDateString('ro-RO', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
                  </p>
                  <p className="text-xs text-blue-700" data-id="element-11249">
                    {filteredViewings.length} vizionări găsite
                  </p>
                </div>
                <button onClick={() => setSelectedDate(null)} className="px-3 py-1 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 transition-colors" data-id="element-11250">
                  Resetează
                </button>
              </motion.div>}

            {filteredViewings.length === 0 ? <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-12 text-center" data-id="element-11251">
                <EyeIcon className="w-16 h-16 mx-auto mb-4 text-slate-300" data-id="element-11252" />
                <h3 className="text-xl font-bold text-slate-700 mb-2" data-id="element-11253">
                  Nicio vizionare găsită
                </h3>
                <p className="text-slate-500 mb-6" data-id="element-11254">
                  {selectedDate ? 'Selectează altă dată din calendar' : 'Încearcă să modifici filtrele sau adaugă o vizionare nouă'}
                </p>
                {!selectedDate && <button onClick={() => setIsAddDrawerOpen(true)} className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-[#357BEB] to-[#21A366] text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all" data-id="element-11255">
                    <PlusIcon className="w-5 h-5" data-id="element-11256" />
                    <span data-id="element-11257">Adaugă Vizionare</span>
                  </button>}
              </div> : <div className="space-y-4" data-id="element-11258">
                {filteredViewings.map((viewing, index) => <motion.div key={viewing.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.05
            }} onClick={() => handleViewDetails(viewing)} className="bg-white rounded-2xl shadow-lg border border-slate-200 hover:shadow-xl hover:border-blue-300 transition-all cursor-pointer overflow-hidden group" data-id="element-11259">
                    {/* Card Header */}
                    <div className="bg-gradient-to-r from-slate-50 to-blue-50 px-6 py-4 border-b border-slate-200" data-id="element-11260">
                      <div className="flex items-center justify-between" data-id="element-11261">
                        <div className="flex items-center space-x-3" data-id="element-11262">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md" data-id="element-11263">
                            <EyeIcon className="w-5 h-5 text-white" data-id="element-11264" />
                          </div>
                          <div data-id="element-11265">
                            <p className="text-xs text-slate-500 font-medium" data-id="element-11266">
                              ID Vizionare
                            </p>
                            <p className="font-mono text-sm font-bold text-slate-900" data-id="element-11267">
                              {viewing.id}
                            </p>
                          </div>
                        </div>
                        {getStatusBadge(viewing.status)}
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="p-6" data-id="element-11268">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-11269">
                        {/* Client Info */}
                        <div className="space-y-3" data-id="element-11270">
                          <div className="flex items-center space-x-2 mb-3" data-id="element-11271">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-11272">
                              <UserIcon className="w-4 h-4 text-blue-600" data-id="element-11273" />
                            </div>
                            <h4 className="text-sm font-bold text-slate-700" data-id="element-11274">
                              Client
                            </h4>
                          </div>
                          <div data-id="element-11275">
                            <p className="text-xs text-slate-500 mb-1" data-id="element-11276">Nume</p>
                            <p className="font-bold text-[#132C53]" data-id="element-11277">
                              {viewing.clientName}
                            </p>
                          </div>
                          <div data-id="element-11278">
                            <p className="text-xs text-slate-500 mb-1" data-id="element-11279">
                              Telefon
                            </p>
                            <a href={`tel:${viewing.clientPhone}`} onClick={e => e.stopPropagation()} className="font-mono text-sm text-blue-600 hover:text-blue-700 font-bold" data-id="element-11280">
                              {viewing.clientPhone}
                            </a>
                          </div>
                        </div>

                        {/* Property Info */}
                        <div className="space-y-3" data-id="element-11281">
                          <div className="flex items-center space-x-2 mb-3" data-id="element-11282">
                            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center" data-id="element-11283">
                              <HomeIcon className="w-4 h-4 text-purple-600" data-id="element-11284" />
                            </div>
                            <h4 className="text-sm font-bold text-slate-700" data-id="element-11285">
                              Proprietate
                            </h4>
                          </div>
                          <div data-id="element-11286">
                            <p className="text-xs text-slate-500 mb-1" data-id="element-11287">
                              ID Proprietate
                            </p>
                            <p className="font-mono font-bold text-[#132C53]" data-id="element-11288">
                              {viewing.propertyId}
                            </p>
                          </div>
                          <div data-id="element-11289">
                            <p className="text-xs text-slate-500 mb-1" data-id="element-11290">
                              Proprietar
                            </p>
                            <p className="font-bold text-slate-700" data-id="element-11291">
                              {viewing.ownerName}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Date & Time + Countdown */}
                      <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl p-4 mb-4" data-id="element-11292">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-11293">
                          <div className="space-y-2" data-id="element-11294">
                            <div className="flex items-center space-x-2" data-id="element-11295">
                              <CalendarIcon className="w-4 h-4 text-blue-600" data-id="element-11296" />
                              <span className="text-xs font-medium text-slate-600" data-id="element-11297">
                                Data & Ora
                              </span>
                            </div>
                            <div className="flex items-center space-x-3" data-id="element-11298">
                              <p className="text-lg font-bold text-slate-900" data-id="element-11299">
                                {viewing.date}
                              </p>
                              <span className="text-slate-400" data-id="element-11300">•</span>
                              <p className="text-lg font-bold text-blue-600" data-id="element-11301">
                                {viewing.time}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center justify-end" data-id="element-11302">
                            <ViewingCountdown date={viewing.date} time={viewing.time} status={viewing.status} data-id="element-11303" />
                          </div>
                        </div>
                      </div>

                      {/* Quick Actions */}
                      <div className="flex items-center justify-end space-x-2 pt-4 border-t border-slate-200" onClick={e => e.stopPropagation()} data-id="element-11304">
                        {(viewing.status === 'scheduled' || viewing.status === 'rescheduled') && <>
                            <button onClick={e => {
                      e.stopPropagation();
                      handleReschedule(viewing);
                    }} className="flex items-center space-x-1 px-3 py-2 rounded-lg bg-orange-50 text-orange-700 hover:bg-orange-100 transition-colors text-sm font-bold" data-id="element-11305">
                              <RefreshCwIcon className="w-4 h-4" data-id="element-11306" />
                              <span data-id="element-11307">Reprogramează</span>
                            </button>
                            <button onClick={e => {
                      e.stopPropagation();
                      handleComplete(viewing);
                    }} className="flex items-center space-x-1 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors text-sm font-bold" data-id="element-11308">
                              <MessageSquareIcon className="w-4 h-4" data-id="element-11309" />
                              <span data-id="element-11310">Finalizează</span>
                            </button>
                            <button onClick={e => {
                      e.stopPropagation();
                      handleCancel(viewing);
                    }} className="flex items-center space-x-1 px-3 py-2 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 transition-colors text-sm font-bold" data-id="element-11311">
                              <XCircleIcon className="w-4 h-4" data-id="element-11312" />
                              <span data-id="element-11313">Anulează</span>
                            </button>
                          </>}

                        {/* Awaiting Response Quick Actions */}
                        {viewing.status === 'awaiting_response' && <>
                            <button onClick={e => {
                      e.stopPropagation();
                      setSelectedViewing(viewing);
                      setIsScheduleSigningModalOpen(true);
                    }} className="flex items-center space-x-1 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors text-sm font-bold" data-id="element-11314">
                              <PenToolIcon className="w-4 h-4" data-id="element-11315" />
                              <span data-id="element-11316">Dorește Semnare</span>
                            </button>
                            <button onClick={e => {
                      e.stopPropagation();
                      handleClientResponse(viewing);
                    }} className="flex items-center space-x-1 px-3 py-2 rounded-lg bg-slate-50 text-slate-700 hover:bg-slate-100 transition-colors text-sm font-bold" data-id="element-11317">
                              <XCircleIcon className="w-4 h-4" data-id="element-11318" />
                              <span data-id="element-11319">Nu Dorește</span>
                            </button>
                          </>}

                        <button onClick={e => {
                    e.stopPropagation();
                    handleViewDetails(viewing);
                  }} className="flex items-center space-x-1 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors text-sm font-bold shadow-md hover:shadow-lg" data-id="element-11320">
                          <span data-id="element-11321">Vezi Detalii</span>
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-11322">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" data-id="element-11323" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </motion.div>)}
              </div>}
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-11324">
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100" data-id="element-11325">
              <h3 className="text-lg font-bold text-[#132C53] mb-4 flex items-center" data-id="element-11326">
                <CalendarIcon className="w-5 h-5 mr-2 text-blue-600" data-id="element-11327" />
                Calendar Rapid
              </h3>
              <MiniCalendar viewings={viewings.map(v => ({
              id: v.id,
              date: v.date,
              time: v.time,
              status: v.status
            }))} onDateClick={handleDateClick} data-id="element-11328" />
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl p-6 shadow-lg text-white relative overflow-hidden" data-id="element-11329">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl" data-id="element-11330"></div>
              <div className="relative z-10" data-id="element-11331">
                <div className="flex items-center space-x-2 mb-2" data-id="element-11332">
                  <CheckSquareIcon className="w-6 h-6" data-id="element-11333" />
                  <h3 className="text-lg font-bold" data-id="element-11334">Checklist Vizionare</h3>
                </div>
                <p className="text-purple-100 text-sm mb-4" data-id="element-11335">
                  Ghid complet pentru pregătirea și desfășurarea vizionărilor
                  profesionale
                </p>
                <button onClick={() => setShowChecklist(!showChecklist)} className="w-full py-2 bg-white/20 backdrop-blur-sm rounded-xl text-sm font-bold hover:bg-white/30 transition-colors" data-id="element-11336">
                  {showChecklist ? 'Ascunde Checklist' : 'Vezi Checklist Complet'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Checklist Section */}
        <AnimatePresence data-id="element-11337">
          {showChecklist && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} className="mt-8" data-id="element-11338">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100" data-id="element-11339">
                <div className="flex items-center justify-between mb-6" data-id="element-11340">
                  <div className="flex items-center space-x-3" data-id="element-11341">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg" data-id="element-11342">
                      <CheckSquareIcon className="w-6 h-6 text-white" data-id="element-11343" />
                    </div>
                    <div data-id="element-11344">
                      <h2 className="text-2xl font-bold text-[#132C53]" data-id="element-11345">
                        Checklist Complet Vizionare
                      </h2>
                      <p className="text-slate-600 text-sm" data-id="element-11346">
                        Urmează pașii pentru o vizionare profesională și
                        eficientă
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setShowChecklist(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors" data-id="element-11347">
                    <XCircleIcon className="w-6 h-6 text-slate-400" data-id="element-11348" />
                  </button>
                </div>
                <ViewingChecklist data-id="element-11349" />
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Modals */}
      <SelectViewingTypeModal isOpen={isTypeModalOpen} onClose={() => setIsTypeModalOpen(false)} onSelectType={handleSelectType} data-id="element-11350" />

      <AddViewingDrawer isOpen={isAddDrawerOpen} onClose={() => {
      setIsAddDrawerOpen(false);
      setIsEditMode(false);
      setEditingViewing(null);
    }} onSubmit={handleDrawerSubmit} hasCompletedRentalTransaction={hasCompletedRentalTransaction} editMode={isEditMode} existingViewing={editingViewing} data-id="element-11351" />

      <RescheduleViewingModal isOpen={isRescheduleModalOpen} onClose={() => {
      setIsRescheduleModalOpen(false);
      setSelectedViewing(null);
    }} onSubmit={handleRescheduleSubmit} viewing={selectedViewing} data-id="element-11352" />

      <CancelViewingModal isOpen={isCancelModalOpen} onClose={() => {
      setIsCancelModalOpen(false);
      setSelectedViewing(null);
    }} onSubmit={handleCancelSubmit} viewing={selectedViewing} data-id="element-11353" />

      <CompleteViewingModal isOpen={isCompleteModalOpen} onClose={() => {
      setIsCompleteModalOpen(false);
      setSelectedViewing(null);
    }} onSubmit={handleCompleteSubmit} viewing={selectedViewing} data-id="element-11354" />

      <AwaitingResponseModal isOpen={isAwaitingResponseModalOpen} onClose={() => {
      setIsAwaitingResponseModalOpen(false);
      setSelectedViewing(null);
      setSkipMainDecision(false);
    }} onScheduleSigning={handleClientResponseScheduleSigning} onReject={handleClientResponseReject} viewing={selectedViewing} skipMainDecision={skipMainDecision} data-id="element-11355" />

      <ScheduleSigningModal isOpen={isScheduleSigningModalOpen} onClose={() => {
      setIsScheduleSigningModalOpen(false);
      setSelectedViewing(null);
    }} onSubmit={handleScheduleSigning} viewing={selectedViewing} data-id="element-11356" />

      <ScheduleSigningModal isOpen={isRescheduleSigningModalOpen} onClose={() => {
      setIsRescheduleSigningModalOpen(false);
      setSelectedViewing(null);
    }} onSubmit={handleRescheduleSigningSubmit} viewing={selectedViewing} data-id="element-11357" />

      <CommissionCollectionModal isOpen={isCommissionModalOpen} onClose={() => {
      setIsCommissionModalOpen(false);
      setSelectedViewing(null);
    }} onSubmit={handleCommissionSubmit} viewing={selectedViewing} data-id="element-11358" />

      <ViewingDetailsDrawer isOpen={isDetailsDrawerOpen} onClose={() => {
      setIsDetailsDrawerOpen(false);
      setSelectedViewing(null);
    }} viewing={selectedViewing} onReschedule={handleReschedule} onCancel={handleCancel} onComplete={handleComplete} onRescheduleSigning={handleRescheduleSigning} onMarkSignedInvoicing={handleMarkSignedInvoicing} onCollectCommission={handleCollectCommission} onEdit={handleEdit} onClientResponse={handleClientResponse} data-id="element-11359" />
    </div>;
}